/**
 * STADIUM CROWD AUDIO — a reverbed ambience bed heard everywhere on the
 * pitch, plus a chant swell that only carries near the stand it comes from.
 *
 * Owner (2026-08-14): "use this audios in the game and give a reverb to make
 * it feel real", and separately "use the audio i marked at some points in
 * the stadium they can be only heard when you go close to those audience".
 * Both were tried as licensed mp3 tracks (stadium-ambience.mp3, crowd-
 * chant.mp3) first. Owner, after hearing it in a match: "there is a music
 * playing in the game not audience noise" — correct, and no amount of
 * gain-gating or reverb changes that, because the FILES are produced songs
 * with a melody; gating what you hear of a song does not stop it sounding
 * like a song the moment it IS audible.
 *
 * Rebuilt on SYNTHESISED noise instead — the exact technique game/audio.ts
 * already uses for its own ambient crowd bed (`ensureCrowd`, `buildNoise`):
 * an AudioBuffer filled with `Math.random() * 2 - 1` has no melody to hear,
 * by construction, however it's filtered or reverbed. Cascaded lowpass for a
 * "wash" texture, bandpass for a "voice/chant" texture — both proven in this
 * codebase already, both genuinely mistakeable for a real crowd because
 * that's structurally what a crowd sounds like (broadband murmur + massed
 * voices in a narrow band), not because either is a sample of one.
 *
 * Deliberately its own synthesis rather than a call into game/audio.ts's
 * SynthEngine — that class is private state owned by AudioSystem, built once
 * per match from practice.ts/soloPractice.ts/testArena.ts, with no seam for
 * a render subsystem to add per-stand positional sources into its graph
 * without changing that constructor's shape. Duplicating ~30 lines of noise-
 * buffer/filter boilerplate here is a smaller, safer change than threading a
 * new dependency through three match-start call sites for one ambience layer.
 *
 * ── ANCHOR POINTS ────────────────────────────────────────────────────────
 * CrowdBillboardSystem.ts buckets the crowd into STAND_COUNT=8 angular
 * sectors (`standIndexFor`) but never exposes their centroids — the 31,635
 * billboard offsets live in a private Float32Array uploaded straight to the
 * GPU. Rather than reach into that, `computeAnchors` derives its own 8
 * evenly-spaced points on an ellipse sized from the SAME margins camera.ts
 * uses to keep the stand geometry (a private, unexported const there) clear
 * of the pitch: STAND_MARGIN_SIDE=7, STAND_MARGIN_END=10,
 * STAND_WALL_STANDOFF=1.5. Audio anchors do not need to land on an exact
 * billboard — they need to sit roughly where the crowd visually is, and an
 * ellipse through the same clearance numbers does that without a dependency
 * on camera.ts's private internals or CrowdBillboardSystem's GPU buffer.
 *
 * Self-contained AudioContext (like ui/menuMusic.ts), not routed through
 * game/audio.ts's AudioSystem or `settings.masterVolume` — that would mean
 * threading a settings reference through renderer.ts's constructor and every
 * one of its call sites for one ambience layer. Fixed, deliberately moderate
 * levels instead; a live volume control is a reasonable follow-up if wanted.
 */
import type { MatchConfig } from '@fg/shared/sim';
import type { RenderState, RenderSubsystem } from './context.js';

const STAND_COUNT = 8; // matches CrowdBillboardSystem.ts's STAND_COUNT
const STAND_MARGIN_SIDE = 7; // camera.ts's private STAND_MARGIN_SIDE
const STAND_MARGIN_END = 10; // camera.ts's private STAND_MARGIN_END
const STAND_WALL_STANDOFF = 1.5; // camera.ts's private STAND_WALL_STANDOFF

// The chant swell only carries near ITS OWN stand: full inside INNER_M,
// silent past OUTER_M, smoothstepped between. A touchline sprint should
// sweep audibly past 2-3 sections, not the whole bowl swelling at once.
const INNER_M = 14;
const OUTER_M = 42;

const AMBIENCE_GAIN = 0.5;
const AMBIENCE_WET = 0.5;
const AMBIENCE_DRY = 0.6;
/** Owner, 2026-08-15: "the audio of audience is very lound reduce it please
 *  when near the boundary" — this is the gain a stand's chant swell reaches
 *  at full proximity (inside INNER_M of its anchor, i.e. hugging the
 *  touchline). Was 0.6; the general always-on ambience bed (AMBIENCE_GAIN)
 *  is untouched — the complaint was specifically about running close to a
 *  stand, which is this proximity-gated layer, not the uniform background. */
const CHANT_GAIN = 0.3;
const REVERB_TAIL_S = 2.4;
const REVERB_DECAY = 2.6;

interface Anchor {
  x: number;
  z: number;
  gain: GainNode;
}

function smoothstep(edge0: number, edge1: number, v: number): number {
  const t = Math.min(1, Math.max(0, (v - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/** A buffer of pure white noise — no melody is representable in this, no
 *  matter how it is filtered or reverbed downstream. Every caller passes a
 *  DIFFERENT `seconds`, so no two loop points across the whole graph ever
 *  land on the same beat (game/audio.ts's `buildNoise` uses the identical
 *  trick — mismatched lengths avoid an audible phase-lock between layers). */
function buildNoiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
  const buf = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * seconds)), ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

/** Exponentially-decayed stereo noise — a standard algorithmic reverb IR
 *  with no external sample to source or attribute. */
function buildImpulseResponse(ctx: AudioContext): AudioBuffer {
  const rate = ctx.sampleRate;
  const length = Math.floor(rate * REVERB_TAIL_S);
  const impulse = ctx.createBuffer(2, length, rate);
  for (let ch = 0; ch < 2; ch++) {
    const data = impulse.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, REVERB_DECAY);
    }
  }
  return impulse;
}

function computeAnchors(config: MatchConfig): { x: number; z: number }[] {
  const rx = config.pitchLength / 2 + STAND_MARGIN_END + STAND_WALL_STANDOFF;
  const rz = config.pitchWidth / 2 + STAND_MARGIN_SIDE + STAND_WALL_STANDOFF;
  return Array.from({ length: STAND_COUNT }, (_, i) => {
    const theta = (i / STAND_COUNT) * Math.PI * 2;
    return { x: Math.cos(theta) * rx, z: Math.sin(theta) * rz };
  });
}

export function createCrowdAudioSystem(): RenderSubsystem {
  let ctx: AudioContext | null = null;
  let anchors: Anchor[] = [];
  let disposed = false;
  let unlockListenerAttached = false;

  const unlock = (): void => {
    if (!ctx || ctx.state !== 'suspended') return;
    void ctx.resume().catch(() => undefined);
  };

  const attachUnlockListener = (): void => {
    if (unlockListenerAttached) return;
    unlockListenerAttached = true;
    const onGesture = (): void => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
      unlock();
    };
    window.addEventListener('pointerdown', onGesture, { once: true });
    window.addEventListener('keydown', onGesture, { once: true });
  };

  // A bandpassed noise "voice/chant" texture per stand — the same shape as
  // game/audio.ts's `voiceL`/`voiceR` layer (bandpass ~550Hz, modest Q), one
  // independent noise buffer per anchor so all 8 stands are decorrelated
  // rather than 8 copies of the same phrase swelling in lockstep.
  const startChantSwell = (audioCtx: AudioContext, anchor: Anchor, seed: number): void => {
    if (disposed) return;
    const src = audioCtx.createBufferSource();
    src.buffer = buildNoiseBuffer(audioCtx, 4.1 + seed * 0.37); // mismatched lengths, see buildNoiseBuffer
    src.loop = true;
    const bp = audioCtx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 520 + seed * 30;
    bp.Q.value = 0.8;
    src.connect(bp).connect(anchor.gain);
    src.start();
  };

  // The general bed: a lowpassed "wash" texture (matching game/audio.ts's
  // wash layer) split dry/wet, the wet leg through the same synthesised
  // reverb tail as the rest of this file — "make it feel real" applied to
  // genuine noise rather than a produced track.
  const startAmbience = (audioCtx: AudioContext): void => {
    if (disposed) return;
    const src = audioCtx.createBufferSource();
    src.buffer = buildNoiseBuffer(audioCtx, 6.2);
    src.loop = true;
    const lp1 = audioCtx.createBiquadFilter();
    lp1.type = 'lowpass';
    lp1.frequency.value = 900;
    const lp2 = audioCtx.createBiquadFilter();
    lp2.type = 'lowpass';
    lp2.frequency.value = 480;

    const dry = audioCtx.createGain();
    dry.gain.value = AMBIENCE_DRY * AMBIENCE_GAIN;
    const wet = audioCtx.createGain();
    wet.gain.value = AMBIENCE_WET * AMBIENCE_GAIN;
    const convolver = audioCtx.createConvolver();
    convolver.buffer = buildImpulseResponse(audioCtx);

    src.connect(lp1).connect(lp2);
    lp2.connect(dry).connect(audioCtx.destination);
    lp2.connect(convolver).connect(wet).connect(audioCtx.destination);
    src.start();
  };

  return {
    name: 'crowdAudio',

    onPitchConfig(config: MatchConfig): void {
      if (disposed) return;
      ctx = new AudioContext();
      anchors = computeAnchors(config).map((p) => {
        const gain = ctx!.createGain();
        gain.gain.value = 0; // set for real by the first update() once distances are known
        gain.connect(ctx!.destination);
        return { x: p.x, z: p.z, gain };
      });

      if (ctx.state === 'suspended') {
        unlock();
        attachUnlockListener();
      }

      anchors.forEach((anchor, i) => startChantSwell(ctx!, anchor, i));
      startAmbience(ctx);
    },

    update(state: RenderState): void {
      if (!ctx || disposed || anchors.length === 0) return;
      const now = ctx.currentTime;
      for (const anchor of anchors) {
        const dx = state.controlledX - anchor.x;
        const dz = state.controlledZ - anchor.z;
        const dist = Math.hypot(dx, dz);
        // 1 near the anchor, 0 past OUTER_M — inverted because smoothstep
        // itself rises with distance, not falls.
        const proximity = 1 - smoothstep(INNER_M, OUTER_M, dist);
        anchor.gain.gain.setTargetAtTime(proximity * CHANT_GAIN, now, 0.15);
      }
    },

    dispose(): void {
      disposed = true;
      void ctx?.close().catch(() => undefined);
      ctx = null;
      anchors = [];
    },
  };
}
